import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useProducts, useSettings } from "@/lib/api";
import { useApp } from "@/lib/store";
import { computeLine, dimsFromVariant, fmtW, newLine, totals } from "@/lib/calc";
import { BUYER_TYPES, COUNTRIES, INCOTERMS } from "@/lib/constants";
import LineItemEditor from "@/components/site/LineItemEditor";
import { Field } from "@/components/site/common";
import { track, useSeo } from "@/lib/seo";

const EMPTY = { name: "", company: "", designation: "", email: "", code: "+1", phone: "", country: "", city: "", buyer_type: "", incoterm: "", port: "", delivery_date: "", notes: "" };

function initLines(prods, sp, basket) {
  const find = (s) => prods.find((p) => p.slug === s);
  const mk = (p, variant, finish) => {
    const l = newLine(p);
    const v = p.variants.find((x) => x.name === variant);
    return { ...l, ...(v ? { variant: v.name, dims: dimsFromVariant(v, l.unit) } : {}), ...(finish && p.finishes.includes(finish) ? { finish } : {}) };
  };
  if (sp.get("from") === "calculator") {
    try { const ls = JSON.parse(sessionStorage.getItem("tij_calc_lines") || "[]"); if (ls.length) return ls.map((l) => ({ ...l, key: Math.random().toString(36).slice(2) })); } catch { /* ignore */ }
  }
  if (sp.get("from") === "basket" && basket.length) return basket.filter((b) => find(b.slug)).map((b) => mk(find(b.slug), b.variant, b.finish));
  const p = find(sp.get("product"));
  if (p) return [mk(p, sp.get("variant"), sp.get("finish"))];
  return [newLine(null)];
}

function validate(lines, b) {
  const le = lines.map((l) => ({ ...(!l.product_slug ? { product: "Select a product" } : {}), ...(!(parseFloat(l.quantity) > 0) ? { quantity: "Enter a quantity" } : {}) }));
  const e = {};
  if (b.name.trim().length < 2) e.name = "Required";
  if (!b.company.trim()) e.company = "Required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.email)) e.email = "Enter a valid email";
  if (b.phone.replace(/\D/g, "").length < 5) e.phone = "Enter a valid phone";
  if (!b.country) e.country = "Required";
  return { le, e, ok: !Object.keys(e).length && le.every((x) => !Object.keys(x).length) };
}

export default function Enquiry() {
  const [sp] = useSearchParams();
  const { data: prods = [] } = useProducts();
  const { data: s } = useSettings();
  const { items: basket, clear } = useApp();
  const [lines, setLines] = useState(null);
  const [b, setB] = useState(EMPTY);
  const [errs, setErrs] = useState({ le: [], e: {} });
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const [hp, setHp] = useState("");
  useSeo("Request a Quote", "Send a structured RFQ with exact product specifications to Tijiya Exports.");
  useEffect(() => { if (prods.length && !lines) setLines(initLines(prods, sp, basket)); }, [prods, lines, sp, basket]);

  const cfg = s?.calculator;
  const est = useMemo(() => totals((lines || []).map((l) => computeLine(prods.find((p) => p.slug === l.product_slug), l, cfg)), cfg), [lines, prods, cfg]);
  const setField = (k) => (e) => {
    const val = e.target.value;
    setB((cur) => ({ ...cur, [k]: val, ...(k === "country" ? { code: (COUNTRIES.find((c) => c[0] === val) || [])[1] || cur.code } : {}) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(lines, b);
    setErrs(v);
    if (!v.ok) { toast.error("Please complete the highlighted fields"); return; }
    setBusy(true);
    try {
      const items = lines.map((l) => {
        const p = prods.find((x) => x.slug === l.product_slug);
        const r = computeLine(p, l, cfg);
        return { product_slug: l.product_slug, product_name: p.name, variant: l.variant, finish: l.finish, dims: l.dims, unit: l.unit, thread_spec: l.thread_spec, size: p.dimension_profile === "STANDARD" ? l.variant : "", quantity: parseFloat(l.quantity), qty_unit: l.qty_unit, attachment: l.attachment, est_weight_kg: r ? +r.net.toFixed(2) : null };
      });
      const { code, phone, ...rest } = b;
      const res = await api.post("/enquiries", { items, buyer: { ...rest, phone: `${code} ${phone}` }, est_total_kg: est.net ? +est.net.toFixed(2) : null, website: hp });
      setRef(res.data.ref);
      track("enquiry_submitted", { lines: items.length });
      if (sp.get("from") === "basket") clear();
      window.scrollTo(0, 0);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  if (ref) return (
    <section data-testid="enquiry-thankyou" className="wrap py-32 max-w-3xl">
      <CheckCircle2 size={40} strokeWidth={1.2} className="text-[var(--brass)]" />
      <p className="eyebrow mt-6">Enquiry received</p>
      <h1 className="text-4xl sm:text-5xl mt-3">Thank you. Our export team will respond with a quotation.</h1>
      <p className="mt-6 text-[var(--graphite)]">Your reference number</p>
      <p data-testid="enquiry-ref" className="serif text-4xl text-[var(--brass)] tabular mt-1">{ref}</p>
      <p className="mt-6 text-sm text-[var(--graphite)]">A copy has been sent to {b.email}.</p>
      <Link to="/products" className="btn btn-outline mt-10">Continue browsing</Link>
    </section>
  );

  return (
    <div data-testid="enquiry-page">
      <section className="wrap pt-16 pb-10 border-b border-[var(--stone)]">
        <p className="eyebrow mb-4">Request for quotation</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl">Request a Quote</h1>
        <p className="mt-5 text-[var(--graphite)] max-w-2xl md:text-lg">Add each product as a line with its size, finish and quantity. Attach a drawing if you are ordering to specification.</p>
      </section>
      <form onSubmit={submit} noValidate className="wrap py-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-5">
          <p className="sec-num">01 · Products</p>
          {!lines && <p className="text-sm">Loading products…</p>}
          {(lines || []).map((l, i) => (
            <LineItemEditor key={l.key} idx={i} line={l} products={prods} withAttachment errors={errs.le[i]}
              onChange={(nl) => { setLines((cur) => cur.map((x, j) => (j === i ? nl : x))); setErrs((cur) => ({ ...cur, le: cur.le.map((x, j) => (j === i ? { ...(x?.product && !nl.product_slug ? { product: x.product } : {}), ...(x?.quantity && !(parseFloat(nl.quantity) > 0) ? { quantity: x.quantity } : {}) } : x)) })); }}
              onRemove={lines.length > 1 ? () => setLines((cur) => cur.filter((_, j) => j !== i)) : null} />
          ))}
          <button type="button" data-testid="add-line-button" className="btn btn-outline btn-sm" onClick={() => setLines((cur) => [...cur, newLine(null)])}><Plus size={14} /> Add another product</button>

          <p className="sec-num pt-10">02 · Buyer details</p>
          <div className="card p-6 grid md:grid-cols-2 gap-5">
            <Field label="Full name" required error={errs.e.name}><input data-testid="buyer-name" className="field" aria-invalid={!!errs.e.name} value={b.name} onChange={setField("name")} /></Field>
            <Field label="Company" required error={errs.e.company}><input data-testid="buyer-company" className="field" aria-invalid={!!errs.e.company} value={b.company} onChange={setField("company")} /></Field>
            <Field label="Designation"><input data-testid="buyer-designation" className="field" value={b.designation} onChange={setField("designation")} /></Field>
            <Field label="Email" required error={errs.e.email}><input data-testid="buyer-email" type="email" className="field" aria-invalid={!!errs.e.email} value={b.email} onChange={setField("email")} /></Field>
            <Field label="Country" required error={errs.e.country}>
              <select data-testid="buyer-country" className="field" aria-invalid={!!errs.e.country} value={b.country} onChange={setField("country")}><option value="">Select…</option>{COUNTRIES.map(([c]) => <option key={c}>{c}</option>)}</select>
            </Field>
            <Field label="Phone" required error={errs.e.phone}>
              <div className="flex gap-2">
                <select data-testid="buyer-phone-code" className="field !w-28" value={b.code} onChange={setField("code")}>{Array.from(new Set(COUNTRIES.map((c) => c[1]))).map((c) => <option key={c}>{c}</option>)}</select>
                <input data-testid="buyer-phone" className="field" aria-invalid={!!errs.e.phone} value={b.phone} onChange={setField("phone")} />
              </div>
            </Field>
            <Field label="City"><input data-testid="buyer-city" className="field" value={b.city} onChange={setField("city")} /></Field>
            <Field label="Buyer type"><select data-testid="buyer-type" className="field" value={b.buyer_type} onChange={setField("buyer_type")}><option value="">Select…</option>{BUYER_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
            <Field label="Delivery Incoterm"><select data-testid="buyer-incoterm" className="field" value={b.incoterm} onChange={setField("incoterm")}><option value="">Select…</option>{INCOTERMS.map((t) => <option key={t}>{t}</option>)}</select></Field>
            <Field label="Destination port"><input data-testid="buyer-port" className="field" value={b.port} onChange={setField("port")} /></Field>
            <Field label="Target delivery date"><input data-testid="buyer-delivery-date" type="date" className="field" value={b.delivery_date} onChange={setField("delivery_date")} /></Field>
            <Field label="Additional notes" className="md:col-span-2"><textarea data-testid="buyer-notes" rows={4} className="field" value={b.notes} onChange={setField("notes")} /></Field>
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="website" value={hp} onChange={(e) => setHp(e.target.value)} />
          </div>
        </div>
        <aside className="lg:col-span-4">
          <div className="card p-6 lg:sticky lg:top-28" data-testid="enquiry-weight-summary">
            <p className="eyebrow">Estimated total weight</p>
            <p data-testid="enquiry-est-weight" className="serif text-5xl mt-3 tabular">{fmtW(est.net, "metric")}</p>
            <p className="text-sm text-[var(--graphite)] mt-1 tabular">{fmtW(est.net, "imperial")} · {est.pcs.toLocaleString()} pcs</p>
            <p className="text-xs text-[var(--graphite)] mt-4">Estimates are indicative. Final weights are confirmed in our quotation.</p>
            <button data-testid="enquiry-submit" disabled={busy || !lines} className="btn btn-brass w-full mt-6">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Submit enquiry</button>
            <p className="text-xs text-[var(--graphite)] mt-3">Fields marked * are required.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
